import React, { ReactNode, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Translation } from './translation';
import { getTranslationsFromFiles } from './translationsFromFiles';

export enum Language {
  NORWEGIAN = 'nb',
  ENGLISH = 'en',
}

// To bcp47 language tags
export const toLocaleTag = function (language: Language): string {
  switch (language) {
    case Language.NORWEGIAN:
      return 'no';
    default:
      return language;
  }
};

export type TranslationPerId = { [id: string]: string };

const mapTranslations = (
  translations: Translation[],
  language: Language,
): TranslationPerId =>
  translations.reduce(
    (acc: { [key: string]: string }, cv: Translation) => ({
      ...acc,
      [cv.id]: cv[language],
    }),
    {},
  );

type TranslationMapPerLang = {
  en: TranslationPerId;
  nb: TranslationPerId;
};

export const localeData = (
  translationsList: Translation[],
): TranslationMapPerLang => ({
  nb: mapTranslations(translationsList, Language.NORWEGIAN),
  en: mapTranslations(translationsList, Language.ENGLISH),
});

export const getLanguageForLocalStorage = (language: Language): string => {
  switch (language) {
    case Language.NORWEGIAN:
      return 'nb';
    case Language.ENGLISH:
      return 'en';
  }
};

export const getLanguageFromLocale = (locale: string | null): Language => {
  switch (locale) {
    case 'nb':
      return Language.NORWEGIAN;
    case 'en':
      return Language.ENGLISH;
    default:
      return Language.NORWEGIAN;
  }
};

export const getLanguageFromLocalStorage = (): Language => {
  const lang = localStorage.getItem('lang');

  return getLanguageFromLocale(lang);
};

interface LanguageStore {
  selectedLanguage: Language;
  setSelectedLanguage: (languange: Language) => void;
}

export const LanguageContext = React.createContext<LanguageStore>({
  selectedLanguage: getLanguageFromLocalStorage(),
  setSelectedLanguage: () => {},
});

const useMessages = () => {
  const translations = getTranslationsFromFiles();
  const language = getLanguageFromLocalStorage();

  return localeData(translations)[language] || localeData(translations).nb;
};

type LanguageState = {
  language: Language;
  languageStore: LanguageStore;
};

export const useLanguageState = (): LanguageState => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    getLanguageFromLocalStorage(),
  );

  const languageStore: LanguageStore = useMemo(() => {
    const changeLanguage = (language: Language) => {
      localStorage.setItem('lang', getLanguageForLocalStorage(language));
      document.documentElement.lang = language;
      setSelectedLanguage(language);
    };

    return {
      selectedLanguage,
      setSelectedLanguage: changeLanguage,
    };
  }, [selectedLanguage]);

  return {
    language: selectedLanguage,
    languageStore,
  };
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { language, languageStore } = useLanguageState();
  const messages = useMessages();

  return (
    <LanguageContext.Provider value={languageStore}>
      <IntlProvider locale={language} key={language} messages={messages}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};
